import { type IntlShape, useIntl } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

import type { AffiliateSocialLinkSchema } from '@/api/gen'
import { isHttpUrl, isSocialPlatform, PLATFORM_LABEL_IDS } from '@/domain/social-links'

/**
 * Read view of a partner's social links, shared by the partner-facing settings
 * page and the admin partner-detail (read-only there). Renders each link's
 * display name (a custom link's own label, else the platform's friendly name)
 * against its URL, with a clean empty state when the partner has none.
 */

type SocialLinksSummaryProps = {
  // Accept `undefined` and treat it as empty HERE so null-safety lives in one
  // place: the `social_links` wire field is hand-typed as required but unverified
  // against a live BE (this FE is gated on gto-wizard#2759 shipping first), so an
  // absent field at runtime must render the empty state, not crash on `.length`.
  links: readonly AffiliateSocialLinkSchema[] | undefined
  className?: string
}

// The custom label when set, else the platform's friendly name. `isSocialPlatform`
// guards a value the client doesn't recognise yet (a BE-added platform before the
// FE catches up) — fall back to the raw wire value rather than a missing i18n key.
const displayName = (
  formatMessage: IntlShape['formatMessage'],
  link: AffiliateSocialLinkSchema,
): string => {
  if (link.platform === 'custom') {
    const label = link.label?.trim()

    return label !== undefined && label !== ''
      ? label
      : formatMessage({ id: PLATFORM_LABEL_IDS.custom })
  }

  return isSocialPlatform(link.platform)
    ? formatMessage({ id: PLATFORM_LABEL_IDS[link.platform] })
    : link.platform
}

export const SocialLinksSummary = ({ links = [], className }: SocialLinksSummaryProps) => {
  const { formatMessage } = useIntl()

  if (links.length === 0) {
    return (
      <Typography
        color="text.secondary"
        data-tst="social-links-empty"
        variant="body2"
      >
        {formatMessage({ id: 'socialLinks.empty' })}
      </Typography>
    )
  }

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      data-tst="social-links-summary"
    >
      {links.map((link) => (
        <div
          key={`${link.platform}-${link.url}`}
          className="flex items-baseline justify-between gap-4"
        >
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {displayName(formatMessage, link)}
          </Typography>
          {isHttpUrl(link.url) ? (
            <a
              className="min-w-0"
              href={link.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Typography
                className="truncate text-right font-medium text-primary-main"
                component="div"
                title={link.url}
                variant="body2"
              >
                {link.url}
              </Typography>
            </a>
          ) : (
            <Typography
              className="truncate text-right font-medium"
              component="div"
              title={link.url}
              variant="body2"
            >
              {link.url}
            </Typography>
          )}
        </div>
      ))}
    </div>
  )
}
